import React, { useEffect, useState } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtINR } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import Paginated from "../../components/Paginated";
import ImageUploader from "../../components/ImageUploader";
import { CheckCircle, Search, QrCode, RefreshCcw } from "lucide-react";

// Row tint by payment method: razorpay = blue, counter = magenta, qr = gray.
const METHOD_STYLE = {
  razorpay: "bg-blue-50 border-l-4 border-blue-400",
  counter: "bg-fuchsia-50 border-l-4 border-fuchsia-400",
  qr: "bg-gray-100 border-l-4 border-gray-400",
};
const METHOD_LABEL = { razorpay: "Razorpay", counter: "Pay at counter", qr: "QR code" };

export default function BillingDeskPage() {
  const { user } = useAuth();
  const isSuper = hasRole(user, "super_admin");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [method, setMethod] = useState("");
  const [status, setStatus] = useState("at_billing_desk");
  const [ending, setEnding] = useState(null); // txn being ended
  const [qrUrl, setQrUrl] = useState("");
  const [qrUpload, setQrUpload] = useState([]);

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (method) p.set("method", method);
    if (status) p.set("status", status);
    api.get(`/api/crm/billing/transactions?${p.toString()}`).then((r) => setRows(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [method, status]);
  useEffect(() => { api.get("/api/crm/payment-qr").then((r) => setQrUrl(r.data?.qr_url || "")).catch(() => {}); }, []);

  const saveQr = async () => {
    if (!qrUpload[0]) { toast.error("Upload the QR image first"); return; }
    try { await api.post("/api/crm/payment-qr", { qr_url: qrUpload[0] }); setQrUrl(qrUpload[0]); setQrUpload([]); toast.success("Payment QR updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Counter</div>
        <h1 className="font-display text-4xl text-maroon">Billing Desk</h1>
        <p className="text-xs text-ash mt-1">Every closed bill lands here. Confirm the money arrived, then end the sale to close the loop.</p>
      </div>

      {/* Super admin: upload the shop payment QR */}
      {isSuper && (
        <div className="bg-white border border-line p-4 mb-6" data-testid="qr-upload-panel">
          <div className="flex items-center gap-2 mb-2"><QrCode size={16} className="text-maroon"/><div className="eyebrow">Shop payment QR</div></div>
          <div className="flex flex-wrap items-center gap-4">
            {qrUrl && <img src={qrUrl} alt="Payment QR" className="w-24 h-24 object-contain border border-line rounded"/>}
            <div className="flex-1 min-w-[220px]">
              <ImageUploader value={qrUpload} onChange={setQrUpload} multiple={false} kind="payment-qr" maxFiles={1} />
              <button onClick={saveQr} className="btn-maroon text-sm mt-2">Save QR</button>
              <p className="text-[10px] text-ash mt-1">Shown to customers for QR payments at the counter and attachable to scheme reminders.</p>
            </div>
          </div>
        </div>
      )}

      {/* Returns → store credit */}
      <ReturnPanel />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-1 border border-line bg-white px-2">
          <Search size={14} className="text-ash"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search txn / customer / product / UTR" className="px-1 py-2 text-sm outline-none min-w-[220px]" data-testid="billing-search"/>
        </div>
        <select className="border border-line px-3 py-2 bg-white text-sm" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="billing-method">
          <option value="">All methods</option>
          <option value="razorpay">Razorpay</option>
          <option value="counter">Pay at counter</option>
          <option value="qr">QR code</option>
        </select>
        <select className="border border-line px-3 py-2 bg-white text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="billing-status">
          <option value="">All statuses</option>
          <option value="at_billing_desk">Open (at desk)</option>
          <option value="completed">Completed</option>
        </select>
        <button onClick={load} className="btn-outline text-sm">Search</button>
      </div>

      <Paginated items={rows} pageSize={15} label="transactions" scroller>
        {(pageRows) => (
          <div className="bg-white border border-line overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-pearl border-b border-line">
                <tr>{["Txn ID", "Customer", "Amount", "Method", "UTR", "Closed by", "Status", ""].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px] whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {pageRows.map((t) => (
                  <tr key={t.id} className={`border-b border-line/60 ${METHOD_STYLE[t.method] || ""}`} data-testid={`txn-${t.id}`}>
                    <td className="p-3 font-mono text-xs">{t.txn_no}</td>
                    <td className="p-3">{t.customer_name || "—"}<div className="text-[10px] text-ash">{t.customer_phone}</div></td>
                    <td className="p-3 font-semibold text-maroon">{fmtINR(t.amount)}</td>
                    <td className="p-3 text-xs">{METHOD_LABEL[t.method] || t.method}</td>
                    <td className="p-3 text-xs font-mono">{t.utr || "—"}</td>
                    <td className="p-3 text-xs">{t.closed_by_name}<div className="text-[10px] text-ash">{(t.closed_by_role || "").replace("_", " ")}</div></td>
                    <td className="p-3">
                      {t.status === "completed"
                        ? <span className="text-[10px] uppercase tracking-widest text-green-700 inline-flex items-center gap-1"><CheckCircle size={12}/> Done</span>
                        : <span className="text-[10px] uppercase tracking-widest text-amber-700">At desk</span>}
                      {t.ended_by_name && <div className="text-[9px] text-ash">by {t.ended_by_name}</div>}
                    </td>
                    <td className="p-3">
                      {t.status !== "completed" && <button onClick={() => setEnding(t)} className="btn-maroon text-xs" data-testid={`end-sale-${t.id}`}>End the sale</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <div className="p-12 text-center text-ash text-sm">No transactions.</div>}
          </div>
        )}
      </Paginated>

      {ending && <EndSaleModal txn={ending} onClose={() => setEnding(null)} onDone={() => { setEnding(null); load(); }} />}
    </div>
  );
}

function EndSaleModal({ txn, onClose, onDone }) {
  const [counterMode, setCounterMode] = useState(txn.method === "counter" ? "cash" : "");
  const [utr, setUtr] = useState(txn.utr || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (txn.method === "counter" && counterMode === "online" && !utr.trim()) { toast.error("Enter the UTR for the online payment"); return; }
    setBusy(true);
    try {
      await api.post(`/api/crm/billing/${txn.id}/end-sale`, { counter_mode: counterMode, utr: utr.trim(), note });
      toast.success("Sale ended — transaction closed");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not end the sale"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-line w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="end-sale-modal">
        <div className="font-display text-2xl text-maroon mb-1">End the sale</div>
        <p className="text-xs text-ash mb-4">{txn.txn_no} · {fmtINR(txn.amount)} · {METHOD_LABEL[txn.method] || txn.method}</p>

        {txn.method === "counter" && (
          <div className="mb-3">
            <div className="eyebrow text-[10px] mb-1">How did the money come in?</div>
            <div className="flex gap-2">
              {["cash", "online"].map((m) => (
                <button key={m} onClick={() => setCounterMode(m)} className={`px-3 py-1.5 text-sm border ${counterMode === m ? "bg-maroon text-pearl border-maroon" : "border-line"}`} data-testid={`counter-mode-${m}`}>{m === "cash" ? "Cash" : "Online (PhonePe/GPay)"}</button>
              ))}
            </div>
          </div>
        )}
        {(txn.method === "qr" || (txn.method === "counter" && counterMode === "online")) && (
          <input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR / reference number" className="w-full border border-line px-3 py-2 bg-pearl text-sm mb-3" data-testid="end-utr"/>
        )}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" rows={2} className="w-full border border-line px-3 py-2 bg-pearl text-sm mb-4"/>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-maroon" data-testid="end-sale-confirm">{busy ? "…" : "End the sale"}</button>
        </div>
      </div>
    </div>
  );
}

// Returns: bring a piece back to stock and (optionally) raise store credit for
// admin approval. Credit never expires and is partially redeemable.
function ReturnPanel() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ label_no: "", customer_phone: "", customer_name: "", credit_mode: "manual", credit_amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.label_no.trim()) { toast.error("Enter the label/barcode being returned"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/crm/inventory/return", { ...f, credit_amount: +f.credit_amount || 0 });
      toast.success(data.credit_id ? `Returned — ₹${(data.amount || 0).toLocaleString("en-IN")} credit raised for approval` : "Returned to stock");
      setF({ label_no: "", customer_phone: "", customer_name: "", credit_mode: "manual", credit_amount: "", reason: "" }); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-line p-4 mb-6" data-testid="return-panel">
      <button onClick={() => setOpen((o) => !o)} className="eyebrow flex items-center gap-2"><RefreshCcw size={14}/> Process a return {open ? "▲" : "▼"}</button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <input placeholder="Label / barcode" className="border border-line px-3 py-2 bg-pearl text-sm" value={f.label_no} onChange={(e) => setF({ ...f, label_no: e.target.value })} data-testid="ret-label"/>
            <input placeholder="Customer phone" maxLength={10} className="border border-line px-3 py-2 bg-pearl text-sm" value={f.customer_phone} onChange={(e) => setF({ ...f, customer_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}/>
            <input placeholder="Customer name" className="border border-line px-3 py-2 bg-pearl text-sm" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })}/>
          </div>
          <div>
            <div className="eyebrow text-[10px] mb-1">Credit value</div>
            <div className="flex gap-2">
              {[["today_rate", "By today's rate"], ["manual", "Manual amount"]].map(([m, label]) => (
                <button key={m} onClick={() => setF({ ...f, credit_mode: m })} className={`px-3 py-1.5 text-sm border ${f.credit_mode === m ? "bg-maroon text-pearl border-maroon" : "border-line"}`} data-testid={`ret-mode-${m}`}>{label}</button>
              ))}
            </div>
            <p className="text-[10px] text-ash mt-1">{f.credit_mode === "today_rate" ? "Credit = the piece's value at today's gold rate (computed from its weight)." : "Type the exact credit amount — e.g. reduced for wear on a piece returned later."}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {f.credit_mode === "manual" && (
              <input type="number" placeholder="Store credit ₹" className="border border-line px-3 py-2 bg-pearl text-sm" value={f.credit_amount} onChange={(e) => setF({ ...f, credit_amount: e.target.value })} data-testid="ret-credit"/>
            )}
            <input placeholder="Reason" className="border border-line px-3 py-2 bg-pearl text-sm" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}/>
          </div>
          <div>
            <button onClick={submit} disabled={busy} className="btn-maroon" data-testid="ret-submit">{busy ? "…" : "Return & raise credit"}</button>
            <span className="text-[11px] text-ash ml-3">Credit needs super/major/admin approval before it reaches the customer.</span>
          </div>
        </div>
      )}
    </div>
  );
}
